import type { BuiltinConnectorDefinition, ConnectorDefinition, ConnectorOrigin, OnOptionsChangeHelpers, ResolvedConnectorDefinition } from '#layers/data-model/types/data-connectors/define';
import type { ConnectorAvailability } from '#layers/data-model/utils/connector-availability';
import type { DataSourceConnectorRef } from '#layers/data-model/utils/data-source-connector-lookup';
import type { ExtensionConnectorDefinition } from '#layers/data-model/utils/extension-connectors';
import type { DataConnectorSummary } from '#layers/extensions/public';
import { defineStore, storeToRefs } from 'pinia';
import { computed } from 'vue';
import { mapValues } from '#layers/base/public';
import { ConnectorSource } from '#layers/data-model/types/data-connectors/define';
import { Availability, connectorAvailability, resolveConnectorAvailableSince } from '#layers/data-model/utils/connector-availability';
import connectorDefinitions from '#layers/data-model/utils/data-connectors';
import { getConnectorForDataSource } from '#layers/data-model/utils/data-source-connector-lookup';
import { createExtensionConnector } from '#layers/data-model/utils/extension-connectors';
import { useExtensionsStore } from '#layers/extensions/public';
import { useTranslationUtils } from '#layers/i18n/public';

export type ConnectorPickerItem = ResolvedConnectorDefinition<any> & {
	publishedAt: string | undefined;
	availability: ConnectorAvailability;
	disabled: boolean;
};

const useDataSourceConnectorsStore = defineStore('dataSourceConnectors', () => {
	const { translateObject } = useTranslationUtils();
	const extensions = useExtensionsStore();

	const builtinConnectors = computed(() => mapValues(connectorDefinitions, (definition) =>
		translateObject(resolveBuiltinConnector(definition), { keys: ['name', 'description'] })));

	const connectorsById = computed<Record<string, ResolvedConnectorDefinition>>(() => ({
		...builtinConnectors.value,
		...Object.fromEntries(extensions.dataConnectors
			.map((summary) => resolveExtensionConnector(summary))
			.map((connector) => [connector.id, connector])),
	}));

	const providerConnectors = computed(() => connectorsByProvider(Object.values(connectorsById.value)));
	const pickerConnectors = computed(() => connectorPickerItems(Object.values(connectorsById.value)));

	/** The connector backing a data source, or `undefined` when Studio doesn't know its provider. */
	function getDataSourceConnector(source: DataSourceConnectorRef) {
		return getConnectorForDataSource(source, connectorsById.value, providerConnectors.value);
	}

	return {
		connectorsById,
		connectorsByProvider: providerConnectors,
		pickerConnectors,
		sortedConnectors: computed(() => sortedConnectors(pickerConnectors.value)),
		getDataSourceConnector,
	};
});

/** The connectors built into Studio and those installed extensions contribute. */
export function useDataSourceConnectors() {
	const store = useDataSourceConnectorsStore();
	const { connectorsById, connectorsByProvider, pickerConnectors, sortedConnectors } = storeToRefs(store);

	return {
		connectorsById,
		connectorsByProvider,
		pickerConnectors,
		sortedConnectors,
		getDataSourceConnector: store.getDataSourceConnector,
	};
}

export function resolveBuiltinConnector<TOptions extends Record<string, any>>(definition: BuiltinConnectorDefinition<TOptions, any>) {
	return resolveConnector(definition, { source: ConnectorSource.Builtin, type: definition.type });
}

export function resolveExtensionConnector(summary: DataConnectorSummary): ExtensionConnectorDefinition {
	return resolveConnector(createExtensionConnector(summary), {
		source: ConnectorSource.Extension,
		permissions: summary.permissions,
	});
}

/**
 * Resolves a connector definition, handling inheritance via `extends`.
 * Recursively resolves base connectors and merges schema and keywords.
 * Automatically chains `onOptionsChange` and `transformOptions` callbacks.
 *
 * Note: Options are stored as raw FormSchema (not resolved). The form component
 * resolves them with live values using resolveFormSchema().
 */
export function resolveConnector<TOptions extends Record<string, any>, TOrigin extends ConnectorOrigin>(
	definition: ConnectorDefinition<TOptions, any>,
	origin: TOrigin,
	visitedConnectors?: string[],
): ResolvedConnectorDefinition<TOptions> & TOrigin {
	visitedConnectors ??= [];

	if (visitedConnectors.includes(definition.id)) {
		throw new Error(`Circular extends chain detected for connector '${definition.id}': ${visitedConnectors.join(' -> ')}`);
	}

	const baseConnector = definition.extends as ConnectorDefinition<any, any> | undefined;

	// Base case: no inheritance
	if (!baseConnector) {
		return {
			...definition,
			// Pass through raw options (FormSchema) - will be resolved with live values in the form
			options: definition.options,
			allKeywords: definition.keywords ?? [],
			provider: definition.provider ?? definition.id,
			preset: null,
			providerMetadata: true,
			baseSchema: definition.schema,
			...origin,
		} as ResolvedConnectorDefinition<TOptions> & TOrigin;
	}

	// Recursively resolve base connector first
	const resolvedBase = resolveConnector(baseConnector, origin, [...visitedConnectors, definition.id]);

	// Pass through raw options, or inherit from base if not defined
	const options = definition.options ?? resolvedBase.options;

	// Chain onOptionsChange: child runs first, then parent
	const childOnOptionsChange = definition.onOptionsChange;
	const parentOnOptionsChange = resolvedBase.onOptionsChange;
	const chainedOnOptionsChange = childOnOptionsChange || parentOnOptionsChange
		? (newVal: TOptions, oldVal: TOptions, helpers: OnOptionsChangeHelpers<TOptions>) => {
				childOnOptionsChange?.(newVal, oldVal, helpers);
				parentOnOptionsChange?.(newVal, oldVal, helpers);
			}
		: undefined;

	// Chain transformOptions: child runs first, then parent
	const childTransformOptions = definition.transformOptions;
	const parentTransformOptions = resolvedBase.transformOptions;
	const chainedTransformOptions = childTransformOptions || parentTransformOptions
		? (opts: TOptions) => {
				const intermediate = childTransformOptions ? childTransformOptions(opts) : opts;
				return parentTransformOptions ? parentTransformOptions(intermediate) : intermediate;
			}
		: undefined;

	return {
		...definition,
		// Pass through raw options (FormSchema) - will be resolved with live values in the form
		options,
		// Store base's raw options so we can resolve context.base.resolvedOptions
		baseOptions: resolvedBase.options,
		// Inherit schema if not specified
		schema: definition.schema ?? resolvedBase.schema,
		// Base schema for validating transformOptions output
		baseSchema: resolvedBase.baseSchema ?? resolvedBase.schema,
		// Merge keywords (own + base's resolved keywords)
		allKeywords: [
			...(definition.keywords ?? []),
			...(resolvedBase.allKeywords ?? []),
		],
		// Provider for submission
		provider: definition.provider ?? resolvedBase.provider,
		preset: definition.id,
		providerMetadata: false,
		// Use chained callbacks
		onOptionsChange: chainedOnOptionsChange,
		transformOptions: chainedTransformOptions,
		// Inherit the base's submit-time guard so presets get its AWS IAM validation
		// without re-declaring it (a no-op unless the user selects IAM auth).
		validateOptions: definition.validateOptions ?? resolvedBase.validateOptions,
		...origin,
	} as ResolvedConnectorDefinition<TOptions> & TOrigin;
}

export function connectorsByProvider(
	connectors: Iterable<ResolvedConnectorDefinition<any>>,
): Record<string, ResolvedConnectorDefinition<any>> {
	const providerConnectors: Record<string, ResolvedConnectorDefinition<any>> = {};

	for (const connector of connectors) {
		if (!connector.providerMetadata) continue;

		const existing = providerConnectors[connector.provider];

		if (existing) {
			throw new Error(
				`Multiple data source connectors provide metadata for provider '${connector.provider}': ${existing.id}, ${connector.id}`,
			);
		}

		providerConnectors[connector.provider] = connector;
	}

	return providerConnectors;
}

export function sortedConnectors<T extends ResolvedConnectorDefinition<any>>(connectors: Iterable<T>) {
	return [...connectors]
		.toSorted((a, b) =>
			compareAvailableConnector(a, b)
			|| compareAvailablePrimaryConnector(a, b)
			|| compareConnectorName(a, b),
		);
}

function compareAvailableConnector(
	a: ResolvedConnectorDefinition<any> & { disabled?: boolean },
	b: ResolvedConnectorDefinition<any> & { disabled?: boolean },
) {
	if (a.disabled === b.disabled) return 0;
	return a.disabled ? 1 : -1;
}

function compareAvailablePrimaryConnector(
	a: ResolvedConnectorDefinition<any> & { disabled?: boolean },
	b: ResolvedConnectorDefinition<any> & { disabled?: boolean },
) {
	if (a.disabled || b.disabled) return 0;
	if (a.providerMetadata === b.providerMetadata) return 0;
	return a.providerMetadata ? -1 : 1;
}

function compareConnectorName(
	a: ResolvedConnectorDefinition<any>,
	b: ResolvedConnectorDefinition<any>,
) {
	return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

export function connectorPickerItems(
	connectors: Iterable<ResolvedConnectorDefinition<any>>,
	now: Date = new Date(),
) {
	return [...connectors].map((connector) => {
		const publishedAt = resolveConnectorAvailableSince(connector.availableSince);
		// Release scheduling covers built-in connectors; installing an extension makes its connector available.
		const availability = connector.source === ConnectorSource.Builtin
			? connectorAvailability({ availableSince: publishedAt }, now)
			: undefined;

		return {
			...connector,
			publishedAt,
			availability,
			disabled: availability === Availability.Soon,
		};
	}) satisfies ConnectorPickerItem[];
}
